import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ShieldAlert, FileSpreadsheet, FileText, ChevronDown, ChevronUp, Loader2, Search, X, Send, Trash2, MessageSquare, Filter, Users, IndianRupee, Building2, BookOpen, Check, RefreshCw } from 'lucide-react';
import { ZohoInvoiceLink, ZohoCrmLeadLink, EnrollmentNoCrmLink, ZohoBooksCustomerButton } from '../../components/ZohoLinks';
import { BookmarkInline, BookmarkMapProvider, useBookmarkMap, bookmarkRowClass } from '../../components/BookmarkButton';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmt = (v) => {
  if (v == null || isNaN(v)) return '-';
  return Number(v).toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

function exportToExcel(rows, columns, filename) {
  const data = rows.map(r => {
    const obj = {};
    columns.forEach(c => { obj[c.label] = c.render ? c.render(r) : r[c.key]; });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), `${filename}.xlsx`);
}

function exportToPdf(rows, columns, title) {
  const doc = new jsPDF({ orientation: columns.length > 6 ? 'landscape' : 'portrait' });
  doc.setFontSize(14);
  doc.text(title, 14, 18);
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 24);
  autoTable(doc, {
    startY: 28,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => c.render ? c.render(r) : (r[c.key] ?? ''))),
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [100, 60, 180], fontSize: 7 },
  });
  doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
}

// ── Comment Section ──
function CommentSection({ studentId, comments: initialComments, reportType, onCommentAdded }) {
  const [comments, setComments] = useState(initialComments || []);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setComments(initialComments || []); }, [initialComments]);

  const addComment = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post('/training/reports/student-comments', {
        student_id: studentId,
        text: newComment.trim(),
        report_type: reportType,
      });
      setComments(prev => [res.data, ...prev]);
      setNewComment('');
      if (onCommentAdded) onCommentAdded(res.data);
      toast.success('Comment added');
    } catch { toast.error('Failed to add comment'); }
    finally { setSubmitting(false); }
  };

  const deleteComment = async (commentId) => {
    try {
      await api.delete(`/training/reports/student-comments/${commentId}`);
      setComments(prev => prev.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div data-testid={`comment-section-${studentId}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Follow-up Comments ({comments.length})
      </p>
      <div className="flex gap-2 mb-2">
        <Textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder="Add a comment about current status, follow-up, action taken..."
          className="text-xs min-h-[36px] h-9 resize-none flex-1"
          data-testid={`comment-input-${studentId}`}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addComment(); } }}
        />
        <Button size="sm" className="h-9 px-3" onClick={addComment} disabled={submitting || !newComment.trim()} data-testid={`comment-submit-${studentId}`}>
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>
      {comments.length > 0 && (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {comments.map(c => (
            <div key={c.id} className="flex items-start gap-2 bg-white border rounded-sm p-2 group">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-700">{c.text}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {c.created_by} &middot; {new Date(c.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                onClick={() => deleteComment(c.id)}
                className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-opacity p-0.5"
                data-testid={`comment-delete-${c.id}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Expandable Row ──
function StudentRow({ row: r, reportType }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const { map } = useBookmarkMap();
  const bmHighlight = bookmarkRowClass(r.enrollment_no, map);

  const latestComment = (r.comments && r.comments.length > 0) ? r.comments[0] : null;

  // Batch numbers as comma-separated for main table
  const batchNumbers = (r.batches || []).map(b => b.batch_number).filter(Boolean);

  return (
    <>
      <TableRow className={`cursor-pointer hover:bg-slate-50/60 ${bmHighlight}`} onClick={() => setExpanded(!expanded)} data-testid={`overdue-row-${r.enrollment_no}`}>
        <TableCell className="w-6 px-2">
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
        </TableCell>
        <TableCell className="text-xs">
          <EnrollmentNoCrmLink enrollmentNo={r.enrollment_no} />
        </TableCell>
        <TableCell>
          <div className="flex items-center">
            <span className="text-sm font-medium">{r.name}</span>
            <BookmarkInline student={{ enrollment_no: r.enrollment_no, id: r.student_id, name: r.name, course: r.course, due_amount: r.due_amount }} />
          </div>
        </TableCell>
        <TableCell className="text-xs text-slate-500">{r.phone}</TableCell>
        <TableCell className="text-xs text-slate-500">
          {batchNumbers.length > 0
            ? batchNumbers.map((bn, i) => <span key={i}>{i > 0 ? ', ' : ''}<span className="text-violet-600 font-mono">#{bn}</span></span>)
            : <span className="text-slate-300">-</span>}
        </TableCell>
        <TableCell className="text-xs text-right">{fmt(r.total_amount)}</TableCell>
        <TableCell className="text-xs text-right text-emerald-600">{fmt(r.paid_amount)}</TableCell>
        <TableCell className="text-sm text-right font-semibold text-red-600">{fmt(r.due_amount)}</TableCell>
        <TableCell className="text-xs text-slate-500">{r.counsellor_name || <span className="text-slate-300">-</span>}</TableCell>
        <TableCell className="text-xs text-slate-400 max-w-[180px] truncate">
          {latestComment ? (
            <span className="flex items-center gap-1" title={latestComment.text}>
              <MessageSquare className="w-3 h-3 text-violet-400 flex-shrink-0" />
              <span className="truncate">{latestComment.text}</span>
            </span>
          ) : (
            <span className="text-slate-300 italic">No comments</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={10} className="bg-slate-50/60 p-0">
            <div className="p-4 space-y-4">
              {/* Zoho Links Row */}
              <div className="flex items-center gap-2 flex-wrap bg-white border rounded-sm px-3 py-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">Enrollment:</span>
                <span className="font-medium text-slate-700 text-xs">{r.enrollment_no || '-'}</span>
                <ZohoCrmLeadLink enrollmentNo={r.enrollment_no} compact />
                <ZohoBooksCustomerButton zohoCustomerId={r.zoho_customer_id} compact />
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                  { l: 'Total Bill', v: fmt(r.total_amount), c: 'text-slate-800' },
                  { l: 'Paid', v: fmt(r.paid_amount), c: 'text-emerald-600' },
                  { l: 'Write Off', v: r.write_off_amount > 0 ? fmt(r.write_off_amount) : '-', c: r.write_off_amount > 0 ? 'text-amber-600' : 'text-slate-400' },
                  { l: 'Pending Dues', v: fmt(r.due_amount), c: 'text-red-600' },
                  { l: 'Email', v: r.email || '-', c: 'text-blue-600', isText: true },
                ].map(s => (
                  <div key={s.l} className="bg-white border rounded-sm p-2.5 text-center">
                    <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{s.l}</p>
                    <p className={`text-sm font-semibold ${s.c} ${s.isText ? 'text-xs truncate' : ''}`}>{s.v}</p>
                  </div>
                ))}
              </div>

              {/* Invoice Details with Zoho Links + Line Items */}
              {(r.invoices && r.invoices.length > 0) ? (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Invoice Details</p>
                  <div className="bg-white border rounded-sm overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/50">
                          <TableHead className="text-[9px] uppercase">Invoice #</TableHead>
                          <TableHead className="text-[9px] uppercase">Date</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Total</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Paid</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Write Off</TableHead>
                          <TableHead className="text-[9px] uppercase text-right">Balance</TableHead>
                          <TableHead className="text-[9px] uppercase">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {r.invoices.map((inv, i) => (
                          <React.Fragment key={i}>
                            <TableRow className="border-b-0">
                              <TableCell className="text-xs font-medium">
                                <ZohoInvoiceLink invoiceNumber={inv.invoice_number || inv.invoice_id} invoiceId={inv.invoice_id} />
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{inv.date || '-'}</TableCell>
                              <TableCell className="text-xs text-right">{fmt(inv.total)}</TableCell>
                              <TableCell className="text-xs text-right text-emerald-600">{fmt(inv.payment_made)}</TableCell>
                              <TableCell className="text-xs text-right">{inv.write_off_amount > 0 ? <span className="text-amber-600">{fmt(inv.write_off_amount)}</span> : <span className="text-slate-300">-</span>}</TableCell>
                              <TableCell className="text-xs text-right font-medium text-red-600">{fmt(inv.balance)}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className={`text-[9px] ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700 border-amber-200' : inv.status === 'overdue' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                  {(inv.status || 'unknown').replace('_', ' ')}
                                </Badge>
                              </TableCell>
                            </TableRow>
                            {/* Line Items sub-rows */}
                            {inv.line_items && inv.line_items.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={7} className="pt-0 pb-2 px-4">
                                  <div className="bg-slate-50 rounded-sm border border-dashed border-slate-200 px-3 py-2">
                                    <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Line Items ({inv.line_items.length})</p>
                                    <div className="space-y-1">
                                      {inv.line_items.map((li, j) => (
                                        <div key={j} className="flex items-center justify-between text-[11px]">
                                          <span className="font-medium text-slate-700 flex-1 min-w-0 truncate">{li.name || '-'}</span>
                                          <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                                            <span className="text-slate-400">Qty: {li.quantity || 1}</span>
                                            <span className="text-slate-500">Rate: {fmt(li.rate)}</span>
                                            {li.discount ? <span className="text-amber-500">Disc: {li.discount}</span> : null}
                                            <span className="font-semibold text-slate-700 w-20 text-right">{fmt(li.item_total)}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">No invoice data available</p>
              )}

              {/* Batch Details with Completion % */}
              {r.batches && r.batches.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Batch Details</p>
                  <div className="space-y-2">
                    {r.batches.map((b, i) => (
                      <div key={i} className="bg-white border rounded-sm overflow-hidden">
                        <div
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50/50 transition-colors"
                          onClick={(e) => { e.stopPropagation(); setExpandedBatch(expandedBatch === b.batch_id ? null : b.batch_id); }}
                        >
                          <div className="flex-1 flex items-center gap-2 flex-wrap text-xs">
                            <span className="font-semibold text-slate-700">{b.batch_name}</span>
                            {b.batch_number && <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">#{b.batch_number}</Badge>}
                            {b.course_name && <span className="text-slate-400">{b.course_name}</span>}
                            {b.centre_name && <span className="text-blue-400">@ {b.centre_name}</span>}
                            <Badge variant="outline" className={`text-[9px] ${b.status === 'Running' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{b.status}</Badge>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {b.completion_percentage != null && (
                              <div className="flex items-center gap-1.5">
                                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${b.completion_percentage >= 75 ? 'bg-emerald-500' : b.completion_percentage >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                                    style={{ width: `${b.completion_percentage}%` }}
                                  />
                                </div>
                                <span className={`text-[10px] font-bold ${b.completion_percentage >= 75 ? 'text-emerald-600' : b.completion_percentage >= 40 ? 'text-amber-600' : 'text-red-500'}`}>
                                  {b.completion_percentage}%
                                </span>
                              </div>
                            )}
                            {expandedBatch === b.batch_id
                              ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                              : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                          </div>
                        </div>
                        {expandedBatch === b.batch_id && (
                          <div className="border-t bg-slate-50/40 px-3 py-2.5 space-y-2">
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                              <div><span className="text-slate-400">Batch Name:</span> <span className="font-medium">{b.batch_name}</span></div>
                              <div><span className="text-slate-400">Batch #:</span> <span className="font-mono font-medium text-violet-600">{b.batch_number || '-'}</span></div>
                              <div><span className="text-slate-400">Course:</span> <span className="font-medium">{b.course_name || '-'}</span></div>
                              <div><span className="text-slate-400">Centre:</span> <span className="font-medium">{b.centre_name || '-'}</span></div>
                              <div><span className="text-slate-400">Status:</span> <span className="font-medium">{b.status}</span></div>
                              <div><span className="text-slate-400">Completion:</span> <span className={`font-bold ${b.completion_percentage != null ? (b.completion_percentage >= 75 ? 'text-emerald-600' : b.completion_percentage >= 40 ? 'text-amber-600' : 'text-red-500') : 'text-slate-400'}`}>{b.completion_percentage != null ? `${b.completion_percentage}%` : 'Not recorded'}</span></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <CommentSection
                studentId={r.student_id}
                comments={r.comments || []}
                reportType={reportType}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ── Active Filter Chips ──
function FilterChips({ filters, filterOptions, onClear }) {
  const chips = [];
  if (filters.course_id) {
    const c = filterOptions.courses?.find(x => x.id === filters.course_id);
    chips.push({ key: 'course_id', label: `Course: ${c?.name || filters.course_id}` });
  }
  if (filters.centre_id) {
    const c = filterOptions.centres?.find(x => x.id === filters.centre_id);
    chips.push({ key: 'centre_id', label: `Centre: ${c?.name || filters.centre_id}` });
  }
  if (filters.batch_statuses && filters.batch_statuses.length > 0) {
    const labels = filters.batch_statuses.map(v => v === 'no_batch' ? 'No Batch' : v);
    chips.push({ key: 'batch_statuses', label: `Status: ${labels.join(', ')}` });
  }
  if (filters.counsellors && filters.counsellors.length > 0) {
    chips.push({ key: 'counsellors', label: `Counsellor: ${filters.counsellors.length <= 2 ? filters.counsellors.join(', ') : `${filters.counsellors.length} selected`}` });
  }
  if (filters.completion_min !== '' || filters.completion_max !== '') {
    const parts = [];
    if (filters.completion_min !== '') parts.push(`${filters.completion_min}%`);
    if (filters.completion_max !== '') parts.push(`${filters.completion_max}%`);
    chips.push({ key: 'completion', label: `Completion: ${parts.join(' - ')}` });
  }
  if (filters.search) {
    chips.push({ key: 'search', label: `Search: "${filters.search}"` });
  }
  if (filters.bookmark_state && filters.bookmark_state !== 'all') {
    chips.push({ key: 'bookmark_state', label: filters.bookmark_state === 'none' ? 'No bookmark added' : 'Has bookmark' });
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map(c => (
        <Badge key={c.key} variant="secondary" className="text-xs gap-1 pl-2 pr-1 py-0.5 bg-violet-50 text-violet-700 border border-violet-200">
          {c.label}
          <button onClick={() => onClear(c.key)} className="hover:bg-violet-200 rounded-full p-0.5"><X className="w-3 h-3" /></button>
        </Badge>
      ))}
      <button onClick={() => onClear('all')} className="text-[10px] text-slate-400 hover:text-red-500 ml-1">Clear all</button>
    </div>
  );
}

// ── Generic Multi-Select Popover ──
function MultiSelectPopover({ options, selected, onToggle, placeholder, searchPlaceholder, labelKey = 'name', valueKey = 'name', testId }) {
  const [searchTerm, setSearchTerm] = useState('');
  const filtered = (options || []).filter(c =>
    (c[labelKey] || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-8 text-xs w-full justify-between font-normal" data-testid={testId}>
          <span className="truncate">
            {selected.length === 0
              ? placeholder
              : selected.length === 1
                ? options.find(o => o[valueKey] === selected[0])?.[labelKey] || selected[0]
                : `${selected.length} selected`}
          </span>
          <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-300" />
            <Input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder || 'Search...'}
              className="h-7 text-xs pl-6"
            />
          </div>
        </div>
        <div className="max-h-48 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-3">No options found</p>
          ) : (
            filtered.map(c => {
              const val = c[valueKey];
              const isSelected = selected.includes(val);
              return (
                <button
                  key={val}
                  onClick={() => onToggle(val)}
                  className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-sm text-xs transition-colors ${
                    isSelected ? 'bg-violet-50 text-violet-700' : 'hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-sm border flex items-center justify-center flex-shrink-0 ${
                    isSelected ? 'bg-violet-600 border-violet-600' : 'border-slate-300'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className="truncate">{c[labelKey]}</span>
                </button>
              );
            })
          )}
        </div>
        {selected.length > 0 && (
          <div className="border-t p-1.5">
            <button
              onClick={() => selected.forEach(s => onToggle(s))}
              className="text-[10px] text-slate-400 hover:text-red-500 w-full text-center"
            >
              Clear selection ({selected.length})
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── Main Page Component ──
function FilteredTable({ rows, reportType, bookmarkState, hasActiveFilters, isOverdue }) {
  const { map } = useBookmarkMap();
  const visibleRows = rows.filter(r => {
    if (bookmarkState === 'none') return !map[r.enrollment_no];
    if (bookmarkState === 'any') return !!map[r.enrollment_no];
    return true;
  });
  const hiddenByBookmark = rows.length - visibleRows.length;

  if (visibleRows.length === 0) {
    return (
      <div className="text-center py-16">
        <ShieldAlert className={`w-10 h-10 mx-auto mb-3 ${isOverdue ? 'text-red-200' : 'text-amber-200'}`} />
        <p className="text-sm text-slate-400">
          {bookmarkState === 'none'
            ? 'Every student currently in view already has an audit bookmark. Clear the Bookmark filter to see others.'
            : bookmarkState === 'any'
              ? 'None of the students in view have an audit bookmark yet.'
              : hasActiveFilters
                ? 'No students match the current filters.'
                : `No students with ${isOverdue ? 'overdue' : 'partially paid'} invoices.`}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {bookmarkState !== 'all' && (
        <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100" data-testid="bookmark-filter-summary">
          Showing <span className="font-semibold text-slate-700">{visibleRows.length}</span> of <span className="font-semibold text-slate-700">{rows.length}</span> students
          {bookmarkState === 'none' && <> with <span className="font-semibold">no bookmark added</span></>}
          {bookmarkState === 'any' && <> that <span className="font-semibold">already have a bookmark</span></>}
          {hiddenByBookmark > 0 && <> • {hiddenByBookmark} hidden by Bookmark filter</>}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10px] uppercase w-6"></TableHead>
            <TableHead className="text-[10px] uppercase">Enroll No</TableHead>
            <TableHead className="text-[10px] uppercase">Student</TableHead>
            <TableHead className="text-[10px] uppercase">Phone</TableHead>
            <TableHead className="text-[10px] uppercase">Batch #</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Total</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Paid</TableHead>
            <TableHead className="text-[10px] uppercase text-right">Pending Due</TableHead>
            <TableHead className="text-[10px] uppercase">Counsellor</TableHead>
            <TableHead className="text-[10px] uppercase">Latest Comment</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleRows.map(r => (
            <StudentRow key={r.student_id} row={r} reportType={reportType} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function OverdueReportPage({ filterType: propFilterType }) {
  const { type } = useParams();
  const reportType = propFilterType || type || 'overdue';
  const isOverdue = reportType === 'overdue';
  const title = isOverdue ? 'Overdue Report' : 'Partially Paid Report';

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ courses: [], centres: [], counsellors: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const [filters, setFilters] = useState({
    course_id: '',
    centre_id: '',
    batch_statuses: [],
    counsellors: [],
    search: '',
    completion_min: '',
    completion_max: '',
    bookmark_state: 'all', // 'all' | 'none' | 'any'
  });

  // Load filter options and sync status once
  useEffect(() => {
    api.get('/training/reports/overdue-filters').then(r => setFilterOptions(r.data)).catch(() => {});
    api.get('/training/zoho/sync-status').then(r => setLastSync(r.data?.last_sync)).catch(() => {});
  }, []);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ filter_type: reportType });
    if (filters.course_id) params.append('course_id', filters.course_id);
    if (filters.centre_id) params.append('centre_id', filters.centre_id);
    if (filters.batch_statuses.length > 0) params.append('batch_statuses', filters.batch_statuses.join(','));
    if (filters.counsellors.length > 0) params.append('counsellors', filters.counsellors.join(','));
    if (filters.search) params.append('search', filters.search);
    if (filters.completion_min !== '') params.append('completion_min', filters.completion_min);
    if (filters.completion_max !== '') params.append('completion_max', filters.completion_max);

    api.get(`/training/reports/overdue-live?${params.toString()}`)
      .then(r => {
        setRows(r.data.rows || []);
        setSummary(r.data.summary || {});
      })
      .catch(() => toast.error('Failed to load report'))
      .finally(() => setLoading(false));
  }, [reportType, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const clearAllFilters = () => {
    setFilters({ course_id: '', centre_id: '', batch_statuses: [], counsellors: [], search: '', completion_min: '', completion_max: '', bookmark_state: 'all' });
  };

  const clearFilter = (key) => {
    if (key === 'all') {
      clearAllFilters();
    } else if (key === 'counsellors' || key === 'batch_statuses') {
      setFilters(prev => ({ ...prev, [key]: [] }));
    } else if (key === 'completion') {
      setFilters(prev => ({ ...prev, completion_min: '', completion_max: '' }));
    } else if (key === 'bookmark_state') {
      setFilters(prev => ({ ...prev, bookmark_state: 'all' }));
    } else {
      setFilters(prev => ({ ...prev, [key]: '' }));
    }
  };

  const toggleMultiSelect = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter(v => v !== value)
        : [...prev[key], value],
    }));
  };

  const hasActiveFilters = filters.course_id || filters.centre_id || filters.batch_statuses.length > 0 || filters.counsellors.length > 0 || filters.search || filters.completion_min !== '' || filters.completion_max !== '' || filters.bookmark_state !== 'all';

  const activeFilterCount = [filters.course_id, filters.centre_id, filters.search].filter(Boolean).length + (filters.batch_statuses.length > 0 ? 1 : 0) + (filters.counsellors.length > 0 ? 1 : 0) + ((filters.completion_min !== '' || filters.completion_max !== '') ? 1 : 0) + (filters.bookmark_state !== 'all' ? 1 : 0);

  const exportColumns = [
    { key: 'enrollment_no', label: 'Enroll No' },
    { key: 'name', label: 'Student' },
    { key: 'phone', label: 'Phone' },
    { key: 'batch_numbers', label: 'Batch #', render: r => (r.batches || []).map(b => b.batch_number).filter(Boolean).join(', ') || '-' },
    { key: 'total_amount', label: 'Total', render: r => fmt(r.total_amount) },
    { key: 'paid_amount', label: 'Paid', render: r => fmt(r.paid_amount) },
    { key: 'due_amount', label: 'Pending Due', render: r => fmt(r.due_amount) },
    { key: 'counsellor_name', label: 'Counsellor' },
    { key: 'completion', label: 'Completion %', render: r => (r.batches || []).map(b => b.completion_percentage != null ? `${b.completion_percentage}%` : '-').join(', ') },
    { key: 'latest_comment', label: 'Latest Comment', render: r => (r.comments && r.comments.length > 0) ? r.comments[0].text : '' },
  ];

  const formatSyncTime = (isoStr) => {
    if (!isoStr) return null;
    try {
      return new Date(isoStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return null; }
  };

  return (
    <BookmarkMapProvider>
    <div className="space-y-5" data-testid={`${reportType}-report-page`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2" data-testid="report-title">
            <ShieldAlert className={`w-5 h-5 ${isOverdue ? 'text-red-500' : 'text-amber-500'}`} />
            {title}
          </h1>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-xs text-slate-400">
              Students with {isOverdue ? 'overdue' : 'partially paid'} invoices from Zoho Books
            </p>
            {lastSync && (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 bg-slate-50 border border-slate-100 rounded-sm px-2 py-0.5" data-testid="last-sync-time">
                <RefreshCw className="w-2.5 h-2.5" />
                Last synced: {formatSyncTime(lastSync.completed_at || lastSync.started_at) || 'Unknown'}
                {lastSync.status === 'success' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-0.5" />}
                {lastSync.status === 'failed' && <span className="w-1.5 h-1.5 rounded-full bg-red-500 ml-0.5" />}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            className="h-8 text-xs"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-3.5 h-3.5 mr-1" />
            Filters
            {hasActiveFilters && <Badge className="ml-1.5 h-4 w-4 p-0 flex items-center justify-center text-[9px] bg-violet-600">{activeFilterCount}</Badge>}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => exportToExcel(rows, exportColumns, `${title.replace(/\s/g, '_')}`)} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => exportToPdf(rows, exportColumns, title)} data-testid="export-pdf-btn">
            <FileText className="w-3.5 h-3.5 mr-1" />PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { l: isOverdue ? 'Overdue Students' : 'Partially Paid Students', v: summary.total_students, c: isOverdue ? 'text-red-600' : 'text-amber-600', icon: Users },
            { l: 'Total Pending', v: fmt(summary.total_due_amount), c: 'text-red-600', icon: IndianRupee },
            { l: 'In Batches', v: summary.with_batches, c: 'text-emerald-600', icon: BookOpen },
            { l: 'No Batch', v: summary.without_batches, c: 'text-amber-600', icon: Building2 },
          ].map(s => (
            <Card key={s.l} className="border p-3 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-sm flex items-center justify-center ${isOverdue ? 'bg-red-50' : 'bg-amber-50'}`}>
                <s.icon className={`w-4 h-4 ${s.c}`} />
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{s.l}</p>
                <p className={`text-lg font-bold ${s.c}`}>{s.v ?? 0}</p>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Filter Bar */}
      {showFilters && (
        <Card className="border p-4 space-y-3" data-testid="filter-panel">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Search</label>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300" />
                <Input
                  value={filters.search}
                  onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  placeholder="Name, enroll no, phone..."
                  className="h-8 text-xs pl-7"
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Course</label>
              <Select value={filters.course_id} onValueChange={v => setFilters(prev => ({ ...prev, course_id: v === '__all__' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-course">
                  <SelectValue placeholder="All Courses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Courses</SelectItem>
                  {filterOptions.courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Centre</label>
              <Select value={filters.centre_id} onValueChange={v => setFilters(prev => ({ ...prev, centre_id: v === '__all__' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-centre">
                  <SelectValue placeholder="All Centres" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Centres</SelectItem>
                  {filterOptions.centres.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Batch Status</label>
              <MultiSelectPopover
                options={[
                  { name: 'Running', value: 'Running' },
                  { name: 'Upcoming', value: 'Upcoming' },
                  { name: 'Completed', value: 'Completed' },
                  { name: 'No Batch', value: 'no_batch' },
                ]}
                selected={filters.batch_statuses}
                onToggle={(v) => toggleMultiSelect('batch_statuses', v)}
                placeholder="All Statuses"
                searchPlaceholder="Search status..."
                labelKey="name"
                valueKey="value"
                testId="filter-batch-status"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Counsellor</label>
              <MultiSelectPopover
                options={filterOptions.counsellors}
                selected={filters.counsellors}
                onToggle={(v) => toggleMultiSelect('counsellors', v)}
                placeholder="All Counsellors"
                searchPlaceholder="Search counsellors..."
                testId="filter-counsellor"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Completion % Range</label>
              <div className="flex items-center gap-2">
                <Select value={filters.completion_min !== '' ? String(filters.completion_min) : '_none'} onValueChange={v => setFilters(prev => ({ ...prev, completion_min: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs w-24" data-testid="filter-completion-min">
                    <SelectValue placeholder="Min %" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Min %</SelectItem>
                    {Array.from({ length: 21 }, (_, i) => i * 5).map(v => (
                      <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-slate-400">to</span>
                <Select value={filters.completion_max !== '' ? String(filters.completion_max) : '_none'} onValueChange={v => setFilters(prev => ({ ...prev, completion_max: v === '_none' ? '' : v }))}>
                  <SelectTrigger className="h-8 text-xs w-24" data-testid="filter-completion-max">
                    <SelectValue placeholder="Max %" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Max %</SelectItem>
                    {Array.from({ length: 21 }, (_, i) => i * 5).map(v => (
                      <SelectItem key={v} value={String(v)}>{v}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1 block">Bookmark</label>
              <Select value={filters.bookmark_state} onValueChange={v => setFilters(prev => ({ ...prev, bookmark_state: v }))}>
                <SelectTrigger className="h-8 text-xs" data-testid="filter-bookmark-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All (any bookmark)</SelectItem>
                  <SelectItem value="none">No bookmark added</SelectItem>
                  <SelectItem value="any">Has bookmark</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end pt-1">
              <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={clearAllFilters} data-testid="clear-all-filters-btn">
                <X className="w-3 h-3 mr-1" /> Clear All Filters
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Active Filter Chips */}
      <FilterChips filters={filters} filterOptions={filterOptions} onClear={clearFilter} />

      {/* Data Table */}
      <Card className="border p-0">
        {loading ? (
          <div className="h-40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
          </div>
        ) : (
          <FilteredTable rows={rows} reportType={reportType} bookmarkState={filters.bookmark_state} hasActiveFilters={hasActiveFilters} isOverdue={isOverdue} />
        )}
      </Card>
    </div>
    </BookmarkMapProvider>
  );
}
